import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormatTimePipe } from './format-time.pipe';
import Swal from 'sweetalert2';

import {
  catchError,
  map,
  switchMap,
  throwError,
  Subject,
  takeUntil,
  Observable,
  forkJoin,
} from 'rxjs';

// Interfaces (se mantienen exactamente igual)
interface ImagenEvaluacion {
  id_imagen_evaluacion: number;
  url_imagen: string;
}

interface Pregunta {
  id_pregunta: number;
  alternativas: Array<{
    id_alternativa: number;
    correcta: number;
  }>;
}

interface EvaluacionResponse {
  id_evaluacion: number;
  nombre: string;
  duracion: number;
  fecha_fin: string;
  imagenes: ImagenEvaluacion[];
  preguntas: Pregunta[];
  rolUsuario: {
    usuario: {
      nombres: string;
      apellidos: string;
    };
  };
  area: {
    nombre: string;
  };
}

interface RespuestaEstudiante {
  id_pregunta: number;
  id_alternativa: number;
  correcto: number;
  id_resultado_evaluacion: number;
}

@Component({
  selector: 'app-evaluacion-estudiante',
  standalone: true,
  imports: [CommonModule, FormsModule, FormatTimePipe],
  templateUrl: './evaluacion-estudiante.component.html',
  styleUrls: ['./evaluacion-estudiante.component.css'],
})
export class EvaluacionEstudianteComponent implements OnInit, OnDestroy {
  // Propiedades públicas (se mantienen exactamente igual)
  id_evaluacion!: number;
  id_rol_usuario!: number;
  evaluacion!: EvaluacionResponse;
  preguntas: Pregunta[] = [];
  respuestas: number[] = [];
  currentQuestionIndex: number = 0;
  tiempoRestante!: number;
  temporizadorActivo: boolean = false;
  puntuacion: number = 0;
  nivelAlcanzado: string = '';
  mostrarPanelPdf = false;
  imagenes: ImagenEvaluacion[] = [];
  imagenActualIndex: number = 0;
  preguntasRespondidas: number = 0;
  preguntasSinResponder: number[] = [];

  // Propiedades privadas (se mantienen exactamente igual)
  private usuario: any;
  private intervalId: any;
  private destroy$ = new Subject<void>();
  private evaluacionEnProgreso: boolean = false;
  private evaluacionEnviada: boolean = false;

  // Constantes para mejor legibilidad
  private readonly WARNING_TIMES = [300, 10]; // 5 minutos y 10 segundos

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {
    this.usuario = JSON.parse(localStorage.getItem('user')!);
    this.id_rol_usuario = this.usuario.id_rol_usuario;
  }

  ngOnInit(): void {
    this.cargarEstadoGuardado();
    this.configurarGuardianNavegacion();
    this.cargarDatosEvaluacion();
    this.configurarProtecciones();
  }

  ngOnDestroy(): void {
    this.limpiarRecursos();
  }

  // Métodos privados (misma funcionalidad pero optimizados)
  private cargarEstadoGuardado(): void {
    const evaluacionGuardada = localStorage.getItem('evaluacionEnProgreso');
    if (!evaluacionGuardada) return;

    try {
      const datos = JSON.parse(evaluacionGuardada);

      // Verificar si es la misma evaluación
      if (datos.id_evaluacion === this.id_evaluacion) {
        // Verificar si el estado no es demasiado viejo (por ejemplo, 24 horas)
        const horasTranscurridas =
          (Date.now() - datos.timestamp) / (1000 * 60 * 60);
        if (horasTranscurridas > 24) {
          localStorage.removeItem('evaluacionEnProgreso');
          return;
        }

        if (datos.tiempoRestante > 0) {
          this.tiempoRestante = datos.tiempoRestante;
          this.respuestas =
            datos.respuestas || new Array(this.preguntas.length).fill(null);
          this.currentQuestionIndex = datos.currentQuestionIndex || 0;
          this.temporizadorActivo = true;
          this.evaluacionEnProgreso = true;
          this.iniciarTemporizador();
          this.actualizarEstadoPreguntas();
        }
      }
    } catch (error) {
      console.error('Error al cargar estado guardado:', error);
      localStorage.removeItem('evaluacionEnProgreso');
    }
  }

  private configurarGuardianNavegacion(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd && this.temporizadorActivo) {
        this.guardarEstadoEvaluacion();
      }
    });
  }

  private cargarDatosEvaluacion(): void {
    this.route.queryParams
      .pipe(
        map((params) => params['id_evaluacion']),
        switchMap((id_evaluacion) => {
          this.id_evaluacion = id_evaluacion;
          return forkJoin({
            evaluacion: this.cargarEvaluacion(),
            imagenes: this.cargarImagenes(),
          });
        }),
        catchError((error) => this.manejarErrorCarga(error))
      )
      .subscribe({
        next: ({ evaluacion, imagenes }) =>
          this.manejarCargaExitosa(evaluacion, imagenes),
        error: (error) => console.error('Error en la suscripción:', error),
      });
  }

  private manejarCargaExitosa(
    evaluacion: EvaluacionResponse,
    imagenes: ImagenEvaluacion[]
  ): void {
    this.evaluacion = evaluacion;
    this.imagenes = imagenes;
    this.preguntas = evaluacion.preguntas;
    this.respuestas = new Array(this.preguntas.length).fill(null);
    this.tiempoRestante = evaluacion.duracion * 60;
  }

  private manejarErrorCarga(error: any): Observable<never> {
    console.error('Error al cargar la evaluación:', error);
    this.router.navigate(['/evaluaciones-estudiante']);
    return throwError(() => error);
  }

  private configurarProtecciones(): void {
    const prevenirAccion = (e: Event) => e.preventDefault();
    ['contextmenu', 'dragstart', 'copy'].forEach((event) => {
      document.addEventListener(event, prevenirAccion);
    });
  }

  private limpiarRecursos(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    // if (this.temporizadorActivo || this.evaluacionEnProgreso) {
    //   localStorage.removeItem('evaluacionEnProgreso');
    // }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private guardarEstadoEvaluacion(): void {
    if (!this.evaluacionEnProgreso) return;

    const estado = {
      id_evaluacion: this.id_evaluacion,
      tiempoRestante: this.tiempoRestante,
      respuestas: this.respuestas,
      currentQuestionIndex: this.currentQuestionIndex,
      timestamp: Date.now(),
      version: '1.0', // Para manejar cambios futuros
    };

    localStorage.setItem('evaluacionEnProgreso', JSON.stringify(estado));
  }

  private iniciarTemporizador(): void {
    this.intervalId = setInterval(() => this.actualizarTemporizador(), 1000);
  }

  private actualizarTemporizador(): void {
    if (this.tiempoRestante <= 0) {
      this.manejarTiempoTerminado();
      return;
    }

    this.tiempoRestante--;
    this.guardarEstadoEvaluacion();
    this.verificarTiemposAdvertencia();
  }

  private manejarTiempoTerminado(): void {
    if (this.evaluacionEnviada) return;

    Swal.fire({
      icon: 'info',
      title: 'Tiempo terminado',
      text: 'Se está enviando tus respuestas...',
      timer: 5000,
      timerProgressBar: true,
    }).then(() => {
      clearInterval(this.intervalId);
      this.temporizadorActivo = false;
      localStorage.removeItem('evaluacionEnProgreso');
      this.evaluarRespuestas();
    });
  }

  private verificarTiemposAdvertencia(): void {
    if (this.WARNING_TIMES.includes(this.tiempoRestante)) {
      const mensaje =
        this.tiempoRestante >= 60
          ? `Le quedan ${Math.floor(this.tiempoRestante / 60)} minutos`
          : `Le quedan ${this.tiempoRestante} segundos`;

      Swal.fire({
        icon: 'warning',
        title: 'Advertencia',
        text: `${mensaje} para completar la evaluación.`,
        timer: 5000,
        timerProgressBar: true,
      });
    }
  }

  private calcularPuntuacion(): number {
    const totalPreguntas = this.preguntas.length;
    const valorPorPregunta = 25 / totalPreguntas; // Esto puede dar decimales periódicos
    const preguntasCorrectas = this.preguntas.reduce(
      (correctas, pregunta, index) => {
        const respuesta = this.respuestas[index];
        const esCorrecta =
          respuesta !== null
            ? pregunta.alternativas.some(
                (a) => a.id_alternativa === respuesta && a.correcta
              )
            : false;
        return correctas + (esCorrecta ? 1 : 0);
      },
      0
    );

    // Redondeamos a 2 decimales para evitar problemas con números periódicos
    return parseFloat((preguntasCorrectas * valorPorPregunta).toFixed(2));
  }

  private calcularNivel(puntuacion: number): string {
    const maxPuntuacion = 25; // Puntaje máximo
    const porcentaje = (puntuacion / maxPuntuacion) * 100;

    if (porcentaje < 44) return 'INICIO'; // < 11 en escala de 25
    if (porcentaje < 64) return 'EN PROCESO'; // < 16 en escala de 25
    if (porcentaje < 84) return 'LOGRO'; // < 21 en escala de 25
    return 'LOGRO DESTACADO'; // >= 21 en escala de 25
  }

  private prepararRespuestasParaEnvio(
    idResultadoEvaluacion: number
  ): RespuestaEstudiante[] {
    return this.respuestas
      .map((respuesta, index) => {
        if (respuesta === null) return null;

        const pregunta = this.preguntas[index];
        const esCorrecta = pregunta.alternativas.some(
          (a) => a.id_alternativa === respuesta && a.correcta
        );

        return {
          correcto: esCorrecta ? 1 : 0,
          id_pregunta: pregunta.id_pregunta,
          id_alternativa: respuesta,
          id_resultado_evaluacion: idResultadoEvaluacion,
        };
      })
      .filter((resp): resp is RespuestaEstudiante => resp !== null);
  }

  private actualizarEstadoPreguntas(): void {
    this.preguntasRespondidas = this.respuestas.filter(
      (r) => r !== null
    ).length;
    this.preguntasSinResponder = this.respuestas
      .map((r, i) => (r === null ? i + 1 : null))
      .filter((i): i is number => i !== null);
  }

  // Métodos públicos (se mantienen exactamente igual)
  iniciarEvaluacion(): void {
    const evaluacionGuardada = localStorage.getItem('evaluacionEnProgreso');

    if (!evaluacionGuardada) {
      if (confirm('¿Desea iniciar la evaluación?')) {
        this.iniciarEvaluacion2();
      }
      return;
    }

    try {
      const datos = JSON.parse(evaluacionGuardada);

      // Si hay una evaluación diferente en progreso, impedir inicio
      if (datos.id_evaluacion !== this.id_evaluacion) {
        Swal.fire({
          icon: 'error',
          title: 'Evaluación en progreso',
          text: 'Ya tienes una evaluación en curso. Debes finalizarla antes de iniciar una nueva.',
          confirmButtonText: 'Entendido',
        }).then(() => {
          this.router.navigate(['/evaluaciones-estudiante']);
        });
        return;
      }

      // Si es la misma evaluación, ofrecer continuar
      if (datos.tiempoRestante > 0) {
        Swal.fire({
          title: 'Evaluación en progreso',
          text: `Tienes una evaluación en curso con ${Math.floor(
            datos.tiempoRestante / 60
          )} minutos y ${
            datos.tiempoRestante % 60
          } segundos restantes. ¿Deseas continuar?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Continuar',
          cancelButtonText: 'Cancelar',
        }).then((result) => {
          if (result.isConfirmed) {
            this.tiempoRestante = datos.tiempoRestante;
            this.iniciarEvaluacion2();
          }
        });
      } else {
        // Tiempo agotado, limpiar estado
        localStorage.removeItem('evaluacionEnProgreso');
        Swal.fire({
          icon: 'info',
          title: 'Tiempo agotado',
          text: 'El tiempo para esta evaluación ha finalizado.',
          confirmButtonText: 'Entendido',
        });
      }
    } catch (error) {
      console.error('Error al procesar los datos de la evaluación:', error);
      localStorage.removeItem('evaluacionEnProgreso');
    }
  }

  iniciarEvaluacion2(): void {
    if (!this.temporizadorActivo) {
      this.temporizadorActivo = true;
      this.evaluacionEnProgreso = true;
      this.iniciarTemporizador();
      this.respuestas = new Array(this.preguntas.length).fill(null);
    }
  }

  siguientePregunta(): void {
    if (this.currentQuestionIndex < this.preguntas.length - 1) {
      this.currentQuestionIndex++;
    }
  }

  anteriorPregunta(): void {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
    }
  }

  siguienteImagen(): void {
    if (this.imagenActualIndex < this.imagenes.length - 1) {
      this.imagenActualIndex++;
    }
  }

  anteriorImagen(): void {
    if (this.imagenActualIndex > 0) {
      this.imagenActualIndex--;
    }
  }

  seleccionarAlternativa(idAlternativa: number, event: MouseEvent): void {
    event.stopPropagation();
    this.respuestas[this.currentQuestionIndex] = idAlternativa;
    this.actualizarEstadoPreguntas();
  }

  finalizar(): void {
    if (this.evaluacionEnviada) return;

    Swal.fire({
      title: 'Procesando evaluación...',
      html: 'Por favor espera mientras se envía tu evaluación',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    this.detenerTemporizador();
    this.procesarFinalizacion();
  }

  private detenerTemporizador(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.temporizadorActivo = false;
    this.evaluacionEnProgreso = false;
    this.evaluacionEnviada = true;
  }

  private procesarFinalizacion(): void {
    this.puntuacion = this.calcularPuntuacion();
    this.nivelAlcanzado = this.calcularNivel(this.puntuacion);

    const resultado = {
      calificacion: this.puntuacion,
      nivel_alcanzado: this.nivelAlcanzado,
      id_evaluacion: this.id_evaluacion,
      id_rol_usuario: this.id_rol_usuario,
    };

    this.enviarResultado(resultado).subscribe({
      next: (response: any) => {
        const respuestasParaEnviar = this.prepararRespuestasParaEnvio(
          response.id_resultado_evaluacion
        );
        const observables = respuestasParaEnviar.map((respuesta) =>
          this.enviarRespuestaIndividual(respuesta)
        );

        forkJoin(observables).subscribe({
          next: () => this.manejarExitoEnvio(),
          error: () => this.manejarErrorRespuestas(),
        });
      },
      error: () => this.manejarErrorEnvio(),
    });
  }

  private manejarExitoEnvio(): void {
    localStorage.removeItem('evaluacionEnProgreso');
    this.evaluacionEnProgreso = false;

    Swal.fire({
      icon: 'success',
      title: 'Evaluación enviada con éxito',
      showConfirmButton: false,
      timer: 1500,
    }).then(() => {
      this.router.navigate(['/evaluaciones-estudiante']);
    });
  }

  private manejarErrorEnvio(): void {
    localStorage.removeItem('evaluacionEnProgreso');
    this.evaluacionEnProgreso = false;

    Swal.fire({
      icon: 'error',
      title: 'Error al enviar la evaluación',
      text: 'Por favor, intente nuevamente.',
    });
  }

  private manejarErrorRespuestas(): void {
    localStorage.removeItem('evaluacionEnProgreso');
    this.evaluacionEnProgreso = false;

    Swal.fire({
      icon: 'error',
      title: 'Error al enviar las respuestas',
      text: 'Por favor, intente nuevamente.',
    });
  }

  evaluarRespuestas(): void {
    if (this.respuestas.some((r) => r === null)) {
      // Swal.fire({
      //   icon: 'warning',
      //   title: 'Advertencia',
      //   text: 'Por favor, intente nuevamente.',
      //   timer: 5000,
      // });
    }
    this.finalizar();
  }

  volver(): void {
    this.router.navigate(['/evaluaciones-estudiante']);
  }

  getLetter(index: number): string {
    return String.fromCharCode(97 + index);
  }

  // Métodos de servicios (se mantienen exactamente igual)
  cargarImagenes(): Observable<ImagenEvaluacion[]> {
    return this.http
      .get<ImagenEvaluacion[]>(
        `${environment.apiUrl}/imagen-evaluacion?id_evaluacion=${this.id_evaluacion}`
      )
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error al obtener las imágenes:', error);
          return throwError(() => error);
        })
      );
  }

  cargarEvaluacion(): Observable<EvaluacionResponse> {
    return this.http
      .get<EvaluacionResponse>(
        `${environment.apiUrl}/evaluacion/${this.id_evaluacion}`
      )
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error al obtener la evaluación:', error);
          return throwError(() => error);
        })
      );
  }

  enviarResultado(resultado: any): Observable<any> {
    return this.http
      .post(`${environment.apiUrl}/resultado-evaluacion`, resultado)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error al enviar el resultado:', error);
          return throwError(() => error);
        })
      );
  }

  enviarRespuestaIndividual(respuesta: RespuestaEstudiante): Observable<any> {
    return this.http
      .post(`${environment.apiUrl}/respuesta-estudiante`, respuesta)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error al enviar la respuesta:', error);
          return throwError(() => error);
        })
      );
  }
}
